/**
 * FE-FIX29 (RULE-MULTIZONE01). Область контроля правила: «Общая область
 * контроля» либо одна/несколько конкретных зон.
 *
 * Authoritative-поле — `zone_ids[]`. Legacy `zone_id` читается только при
 * гидрации. Сериализация (ТЗ №03): general → zone_ids=[] и zone_id=null;
 * specific → только zone_ids, ключ zone_id не отправляется.
 *
 * Multi-zone counts у backend аддитивны: cross-zone re-ID не выполняется, и
 * пересекающиеся полигоны могут посчитать одну физическую детекцию дважды.
 * UI не имеет права утверждать, что это «тот же человек».
 */

import { GENERAL_ZONE_LABEL } from '../constants/violationConstants';

export const ZONE_SCOPE_GENERAL = 'general';
export const ZONE_SCOPE_SPECIFIC = 'specific';

export const EMPTY_ZONE_SELECTION = { scope: ZONE_SCOPE_GENERAL, zoneIds: [] };

const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

/**
 * Состояние формы из правила. Старые правила (одиночный zone_id без zone_ids)
 * разворачиваются в zone_ids=[zone_id] — смысл правила от этого не меняется.
 */
export const fromRuleZones = (rule) => {
  const ids = asArray(rule?.zone_ids);
  if (ids.length > 0) return { scope: ZONE_SCOPE_SPECIFIC, zoneIds: ids };

  const legacy = rule?.zone_id;
  if (legacy) return { scope: ZONE_SCOPE_SPECIFIC, zoneIds: [legacy] };

  return { ...EMPTY_ZONE_SELECTION };
};

export const toZonesDto = (selection) => {
  const ids = asArray(selection?.zoneIds);
  if (selection?.scope !== ZONE_SCOPE_SPECIFIC || ids.length === 0) {
    return { zone_id: null, zone_ids: [] };
  }
  return { zone_ids: ids };
};

export const validateZoneSelection = (selection) => {
  if (selection?.scope !== ZONE_SCOPE_SPECIFIC) return { valid: true, error: '' };
  if (asArray(selection.zoneIds).length === 0) {
    return { valid: false, error: 'Выберите хотя бы одну зону или переключитесь на общую область контроля' };
  }
  return { valid: true, error: '' };
};

/**
 * Имена показываем только как presentation: идентичность зоны — stable
 * zone_id. Пропавшую из каталога зону печатаем её же идентификатором, чтобы
 * администратор видел, какую именно зону чинить (activation вернёт
 * rule_zone_unavailable).
 */
export const getZoneNames = ({ zoneIds = [], zones = [], fallbackName = '' } = {}) =>
  asArray(zoneIds).map((zoneId) => {
    const zone = (Array.isArray(zones) ? zones : []).find((item) => item?.zone_id === zoneId);
    return zone?.display_name || fallbackName || zoneId;
  });

/**
 * Подпись области контроля для списка/карточки правила и для исторического
 * snapshot события. `zone_scope` из ответа V2 имеет приоритет над эвристикой
 * по массиву: пустой zone_ids при scope=specific — это состояние backend, а не
 * повод молча показать «Общая область контроля».
 */
export const getZoneScopeLabel = ({
  zoneIds,
  zoneId,
  zoneScope,
  zones = [],
  fallbackName = '',
} = {}) => {
  const ids = asArray(zoneIds);
  const effective = ids.length > 0 ? ids : (zoneId ? [zoneId] : []);

  if (zoneScope === ZONE_SCOPE_GENERAL || effective.length === 0) return GENERAL_ZONE_LABEL;

  return getZoneNames({ zoneIds: effective, zones, fallbackName })
    .map((name) => `«${name}»`)
    .join(', ');
};

/** Код deterministic-отказа активации из ТЗ §32. */
export const RULE_ZONE_UNAVAILABLE = 'rule_zone_unavailable';

export const isZoneUnavailableError = (error) => {
  const data = error?.response?.data;
  const detail = data?.detail;
  const code = (typeof detail === 'string' ? detail : detail?.code) || data?.code || '';
  return String(code).toLowerCase().includes(RULE_ZONE_UNAVAILABLE);
};

export const ZONE_UNAVAILABLE_MESSAGE =
  'Одна из зон правила недоступна или удалена. Откройте правило, исправьте выбранные зоны и активируйте его заново.';
